import { IScope, IPromise, IQService, IController } from "angular";
import {
  checkIfCustomFieldIsNew,
  CustomFieldService,
  ICustomField,
  ICustomFieldReorderPayload,
  ICustomFieldReorderResponse,
  ICustomFieldsStoreState,
  ICustomFieldWSData,
  reorderInitialCustomFields,
} from "@gtmhub/customFields";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IPluginsStoreState } from "@gtmhub/plugins";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { IDndDroppedParams, IDndInsertedParams, IOnDndDroppedCbResponse } from "@gtmhub/shared/components";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { CurrentUserService } from "@gtmhub/users";
import { AnalyticsService } from "@gtmhub/core/analytics";
import dayjs from "dayjs";

const DEFAULT_ACTIVE_TAB = "goal";

type ManageOrderActiveTab = "goal" | "metric";

const bindStateToScope = (state: ICustomFieldsStoreState & ISessionsStoreState & IPluginsStoreState) => {
  return {
    isFetched: state.customFields.isFetched && state.sessions.isFetched,
    customFields: state.customFields,
    sessions: state.sessions.items,
  };
};

export interface ICustomFieldsScope extends IScope, IExtendedRootScopeService, ReturnType<typeof bindStateToScope> {
  cfTargetSessions: IPlanningSession[];
  showSystemFields: boolean;
  isGoalCustomFieldsViewed: boolean;
  isMetricCustomFieldsViewed: boolean;
  lastUpdated: string;
  currentCustomFields: ICustomField[];
  currentActiveTab: ManageOrderActiveTab;
  changeActiveTab(tab: ManageOrderActiveTab): void;
  reorderCustomField(params: IDndDroppedParams<ICustomField>): void;
  onReorderSuccess(params: IDndDroppedParams<ICustomField>): void;
  isCustomFieldNew(item: ICustomField): boolean;
  toggleCustomFields(args?: { hide: boolean }): void;
}

export class ManageOrderCtrl implements IController {
  public static $inject = [
    "$scope",
    "$ngRedux",
    "CustomFieldActions",
    "PlanningSessionsActions",
    "$q",
    "UIErrorHandlingService",
    "CustomFieldService",
    "CurrentUserService",
    "AnalyticsService",
  ];

  private currentDate: string;
  private currentActiveTab: ManageOrderActiveTab;
  private metricInitialOrderLoaded = false;
  private goalInitialOrderLoaded = false;
  private shouldListenToWebSocket = true;

  constructor(
    private $scope: ICustomFieldsScope,
    private $ngRedux: INgRedux,
    private customFieldsActions: CustomFieldActions,
    private sessionActions: PlanningSessionsActions,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private customFieldService: CustomFieldService,
    private currentUserService: CurrentUserService,
    private analyticsService: AnalyticsService
  ) {
    this.$scope.showSystemFields = false;
    this.$scope.lastUpdated = "";
    this.currentDate = dayjs.utc().toISOString();
    this.currentActiveTab = DEFAULT_ACTIVE_TAB;
    this.$scope.currentActiveTab = DEFAULT_ACTIVE_TAB;

    this.$scope.changeActiveTab = this.changeActiveTab;
    this.$scope.reorderCustomField = this.reorderCustomField;
    this.$scope.onReorderSuccess = this.onReorderSuccess;
    this.$scope.toggleCustomFields = this.toggleCustomFields;

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$scope.$on("accountCustomFieldsUpdated", this.onAccountCustomFieldsUpdated);

    this.$ngRedux.dispatch(this.customFieldsActions.getCustomFields());
    this.$ngRedux.dispatch(this.sessionActions.getSessions());
  }

  // According to the UX design the old custom fields should be sorted by orderId while the new ones should be appended to bottom of the list.
  // That's why we split the array and sort it separately for initial loading.
  public stateInit(): IPromise<unknown> {
    return this.$scope
      .watchUntil(() => this.$scope.isFetched)
      .then(() => {
        this.$scope.isCustomFieldNew = (item: ICustomField) => checkIfCustomFieldIsNew(item, this.$scope.lastUpdated);
        this.updateCustomFieldsSettings();
        this.changeActiveTab(this.currentActiveTab);
      });
  }

  changeActiveTab = (tab: ManageOrderActiveTab): void => {
    this.currentActiveTab = tab;
    this.$scope.currentActiveTab = tab;
    const currentCustomFields = this.$scope.customFields.map[tab];
    this.analyticsService.track("Custom Fields Loaded", { targetType: this.currentActiveTab });
    if (tab === "goal" && !this.goalInitialOrderLoaded) {
      this.$scope.currentCustomFields = reorderInitialCustomFields(currentCustomFields, this.$scope.lastUpdated);
      this.goalInitialOrderLoaded = true;
    } else if (tab === "metric" && !this.metricInitialOrderLoaded) {
      this.$scope.currentCustomFields = reorderInitialCustomFields(currentCustomFields, this.$scope.lastUpdated);
      this.metricInitialOrderLoaded = true;
    } else {
      this.$scope.currentCustomFields = angular.copy(currentCustomFields || []).sort((a, b) => a.orderId - b.orderId);
      this.onTabView(this.currentActiveTab);
    }

    this.toggleCustomFields({ hide: true });
  };

  toggleCustomFields = (args?: { hide: boolean }): void => {
    this.$scope.showSystemFields = !this.$scope.showSystemFields;
    if (args && args.hide) {
      this.$scope.showSystemFields = false;
    }
  };

  private onTabView = (tab: ManageOrderActiveTab) => {
    if (tab === "goal") {
      this.$scope.isGoalCustomFieldsViewed = true;
    } else {
      this.$scope.isMetricCustomFieldsViewed = true;
    }
  };

  private onAccountCustomFieldsUpdated = (evt, data: ICustomFieldWSData) => {
    const isUpdatingTheSameTab = data?.targetTypes.length !== 0 && data.targetTypes[0] === this.currentActiveTab;

    if (this.shouldListenToWebSocket && isUpdatingTheSameTab) {
      this.$scope.currentCustomFields = data.items.sort((a, b) => a.orderId - b.orderId);
    }

    this.shouldListenToWebSocket = true;
  };

  private updateCustomFieldsSettings = (): void => {
    const customFieldsSettings = this.currentUserService.getUserSetting("customFieldsSettings");

    if (customFieldsSettings && customFieldsSettings.lastUpdated) {
      this.$scope.lastUpdated = customFieldsSettings.lastUpdated;
    } else {
      this.$scope.lastUpdated = this.currentDate;
    }

    this.currentUserService.setUserSetting({
      customFieldsSettings: {
        lastUpdated: this.currentDate,
      },
    });
  };

  private reorderCustomField = (params: IDndDroppedParams<ICustomField>): IPromise<IOnDndDroppedCbResponse<ICustomFieldReorderResponse>> => {
    const { items, item, oldIndex, newIndex } = params;
    const movedItem: ICustomFieldReorderPayload = {
      moved: item._id,
    };

    const firstElementIndex = 0;
    const lastElementIndex = items.length - 1;

    switch (newIndex) {
      case firstElementIndex: {
        movedItem.next = items[firstElementIndex]._id;
        break;
      }
      case lastElementIndex: {
        movedItem.previous = items[lastElementIndex]._id;
        break;
      }
      default: {
        let indexPrevious: number;
        let indexNext: number;

        if (oldIndex < newIndex) {
          indexPrevious = newIndex;
          indexNext = newIndex + 1;
        } else {
          indexPrevious = newIndex - 1;
          indexNext = newIndex;
        }

        movedItem.previous = items[indexPrevious]._id;
        movedItem.next = items[indexNext]._id;
      }
    }

    this.shouldListenToWebSocket = false;
    return this.customFieldService.reorderCustomField(movedItem).then(
      (data) => {
        return this.$q.resolve({ shouldProceed: true, data });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return this.$q.resolve({ shouldProceed: false });
      }
    );
  };

  private onReorderSuccess = (params: IDndInsertedParams<ICustomFieldReorderResponse, ICustomField>): void => {
    const customFieldToBeUpdated = this.$scope.currentCustomFields.find((item) => item._id === params.item._id);
    if (customFieldToBeUpdated) {
      customFieldToBeUpdated.orderId = params.data.orderId;
    }
    this.onTabView(this.currentActiveTab);
  };
}
